import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../L1StaticData/edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { useTranslation } from "react-i18next";

const filterL1TransactionsSchema = z.object({
  l1_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  l1_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  l1_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  l1_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  l1_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
});

const filterL1TransactionsFields = filterL1TransactionsSchema.keyof().enum;

export type FilterL1TransactionsSchema = z.infer<
  typeof filterL1TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterL1TransactionsSchema>[] = [
  {
    name: filterL1TransactionsFields.l1_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterL1TransactionsFields.l1_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterL1TransactionsFields.l1_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterL1TransactionsFields.l1_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterL1Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.L1_TRANSACTIONS;

  const fields: Field[] = [
    {
      name: filterL1TransactionsFields.l1_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.l1_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterL1TransactionsSchema>(
        portfolioDepFields,
        filterL1TransactionsFields.l1_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterL1TransactionsFields.l1_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.l1_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterL1TransactionsSchema>(
        portfolioDepFields,
        filterL1TransactionsFields.l1_portfolio_account_name_contains,
      ),
    },
    {
      name: filterL1TransactionsFields.l1_portfolio_name_contains,
      label: t(`${tableName}.searchbox.l1_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterL1TransactionsSchema>(
        portfolioDepFields,
        filterL1TransactionsFields.l1_portfolio_name_contains,
      ),
    },
    {
      name: filterL1TransactionsFields.l1_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.l1_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterL1TransactionsSchema>(
        portfolioDepFields,
        filterL1TransactionsFields.l1_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterL1TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterL1TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterL1TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${tableName}.searchbox.asset_class_1_equals`),
      assetSubclassLabel: t(`${tableName}.searchbox.asset_class_2_equals`),
      client: Clients.L1,
      staticDataTableName: Tables.L1_STATIC_DATA,
      language: "fr",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterL1TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.L1,
      table_name: Tables.L1_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: filterL1TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.L1,
      table_name: Tables.L1_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterL1TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.L1,
      table_name: Tables.L1_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterL1TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterL1TransactionsFields.date_from },
      to: { name: filterL1TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterL1TransactionsFields.create_from },
      to: { name: filterL1TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.L1}
      schema={filterL1TransactionsSchema}
      tableName={Tables.L1_TRANSACTIONS}
      fields={fields}
    />
  );
}
